/*
 * Main component for the control charts
 * It contains the carrousels for the ECL and the ECL gaps
 * It then displays several charts with the data received from the API.
 */

import React, { useState, useEffect, useRef } from "react";
import { useAuth } from "hooks/useAuth";
import { osirisApi } from "services/api/osiris";
import { Row, Col, Carousel } from "react-bootstrap";
import { SelectBox } from "devextreme-react/select-box";
import {
  getClientPalette,
  defaultPalette,
} from "../../../../../config/clientPalettes";
import { useGroupedItems } from "hooks/useGroupedItems";
import EmptyCard from "components/common/pyramid/EmptyCard";
import PyramidCard from "components/common/pyramid/PyramidCard";
import EclGapBySecurity from "./EclGapBySecurity";
import EclGapByRating from "./EclGapByRating";
import EclGapByInstrumentType from "./EclGapByInstrumentType";
import EclGapBySector from "./EclGapBySector";
import EclGapByBucket from "./EclGapByBucket";
import EclGapDistribution from "./EclGapDistribution";
import EclActuarialGapDistribution from "./EclActuarialGapDistribution";

/**
 * CarouselSection Component
 *
 * Responsible for rendering two independent carousels:
 * 1. ECL (Expected Credit Loss) data carousel - shows Osiris and CSA data
 * 2. ECL Gap data carousel - shows total and absolute gap values
 *
 * @param {Object} eclOsirisData - ECL data from Osiris system
 * @param {Object} eclGroupData - ECL data from CSA calculations
 * @param {Object} eclGapTotal - Total gap between Osiris and CSA ECL values
 * @param {Object} eclGapTotalAbs - Absolute gap values (ignoring positive/negative)
 */
const CarouselSection = ({
  eclOsirisData,
  eclGroupData,
  eclGapTotal,
  eclGapTotalAbs,
}) => {
  const carouselSettings = {
    variant: "dark",
    interval: 5000,
    controls: true,
    indicators: false,
  };

  return (
    <>
      {/* First Carousel - Shows ECL data (Osiris and CSA) */}
      <Col md={4}>
        <PyramidCard className="h-100">
          <Carousel {...carouselSettings}>
            <Carousel.Item>
              <EmptyCard chartData={eclOsirisData} />
            </Carousel.Item>
            <Carousel.Item>
              <EmptyCard chartData={eclGroupData} />
            </Carousel.Item>
          </Carousel>
        </PyramidCard>
      </Col>

      {/* Second Carousel - Shows Gap data (Total and Absolute) */}
      <Col md={4}>
        <PyramidCard className="h-100">
          <Carousel {...carouselSettings}>
            <Carousel.Item>
              <div className="h-100">
                <EmptyCard chartData={eclGapTotal} />
              </div>
            </Carousel.Item>
            <Carousel.Item>
              <div className="h-100">
                <EmptyCard chartData={eclGapTotalAbs} />
              </div>
            </Carousel.Item>
          </Carousel>
        </PyramidCard>
      </Col>
    </>
  );
};

/**
 * ControlCharts Component
 *
 * Main dashboard component for displaying ECL control charts and comparisons.
 * Shows the difference (gap) between expected and actual credit loss values
 * across various dimensions like securities, ratings, sectors, etc.
 */
const ControlCharts = () => {
  // State management for chart data and UI controls
  const [chartData, setChartData] = useState({
    eclGapTotal: null, // Total ECL gap
    eclGapTotalAbs: null, // Absolute ECL gap (magnitude only)
    securityData: [], // Security-level breakdown
    ratingsData: [], // Ratings breakdown
    bucketsData: [], // Bucket (stage) breakdown
    instrumentTypesData: [], // Instrument type breakdown
    sectorsData: [], // Sector breakdown
    gapDistributionData: [], // Distribution of ECL gaps
    actuarialGapDistributionData: [], // Distribution of actuarial gaps
    eclOsirisData: null, // ECL data from Osiris
    eclGroupData: null, // ECL data from CSA
  });
  const [computations, setComputations] = useState([]);
  const [selectedComputation, setSelectedComputation] = useState(null);
  const [currentPalette, setCurrentPalette] = useState(defaultPalette);
  const [isLoading, setIsLoading] = useState(true);

  // Authentication and API access
  const { accessToken, account, companyList, userList } = useAuth();
  const api = osirisApi();

  // Grouping computations by user
  const groupedComputations = useGroupedItems(
    computations,
    userList,
    companyList
  );

  // Get the group display name for the selected computation
  const selectedComputationItem = computations.find(
    (item) => item.id === selectedComputation
  );
  const selectedGroupDisplayName =
    selectedComputationItem && companyList && companyList.length > 0
      ? companyList.find(
        (company) => company.id === selectedComputationItem.groupId
      )?.displayName
      : null;

  /**
   * Effect to update the color palette when the user account changes
   * Maintains consistent branding per client
   */
  useEffect(() => {
    if (account && companyList && companyList.length > 0) {
      const newPalette = getClientPalette(companyList[0].id || "default");
      setCurrentPalette(newPalette);
    }
  }, [account, companyList]);

  /**
   * Effect to fetch available computations when component mounts
   * Retrieves only completed/ready computations and sorts by creation date
   */
  useEffect(() => {
    const fetchComputations = async () => {
      try {
        if (!accessToken) {
          return;
        }

        const data = await api.getComputations({
          onlyReady: true,
          includeUserTransitiveGroups: true,
        });

        if (data) {
          const computations = Array.isArray(data) ? data : data.result || [];
          // Sort computations by creation date (newest first)
          const sortedComputations = [...computations].sort(
            (a, b) => new Date(b.createdAt) - new Date(a.createdAt)
          );
          setComputations(sortedComputations);

          // Auto-select the most recent computation
          if (sortedComputations.length > 0) {
            setSelectedComputation(sortedComputations[0].id);
          }
        } else {
          console.error("Unexpected API response format:", data);
        }
      } catch (error) {
        console.error("Error in the response:", error);
      } finally {
        setIsLoading(false);
      }
    };

    fetchComputations();
  }, [accessToken]);

  /**
   * Effect to fetch control chart data when the selected computation changes
   * Transforms the API response data into the format expected by chart components
   */
  useEffect(() => {
    const fetchControlCharts = async () => {
      if (!selectedComputation || !accessToken) return;

      try {
        // Fetch control chart data for the selected computation
        const data = await api.getControlCharts(selectedComputation);

        if (data) {
          const result =
            Array.isArray(data) && data.length > 0 ? data[0].Items : [];
          // Transform API data into component-friendly format
          const transformedData = {
            // Security breakdown data - Detailed view of ECL gaps per security
            securityData:
              result
                .find((chart) => chart.Name === "EclGapBySecurity")
                ?.Data.map((item) => ({
                  name: item.SecurityLib,
                  isin: item.Isin,
                  value: item.EclGapTotal,
                  percentage: item.PctGap,
                  cumulativePercentage: item.CumPctGap,
                })) || [],

            // Rating breakdown - Compare ECL by credit rating
            ratingsData:
              result
                .find((chart) => chart.Name === "EclGapByRatings")
                ?.Data.map((item) => ({
                  rating: item.ClientLib,
                  eclOsiris: item.EclOsiris,
                  eclUser: item.EclUser,
                  eclGap: item.EclGapTotal,
                })) || [],

            // Bucket breakdown - ECL gaps by IFRS9 stage/bucket
            bucketsData:
              result
                .find((chart) => chart.Name === "EclGapByBuckets")
                ?.Data.map((item) => ({
                  name: item.BucketIdClientLib,
                  value: item.EclGapTotal,
                })) || [],

            // Instrument type breakdown - ECL gaps by financial instrument type
            instrumentTypesData:
              result
                .find((chart) => chart.Name === "EclGapByInstrumentTypes")
                ?.Data.map((item) => ({
                  name: item.InstrumentType,
                  value: item.EclGapTotal,
                })) || [],

            // Sector breakdown - ECL gaps by industry sector
            sectorsData:
              result
                .find((chart) => chart.Name === "EclGapBySectors")
                ?.Data.map((item) => ({
                  name: item.Sector,
                  value: item.EclGapTotal,
                })) || [],

            // Gap distribution - Histogram of ECL gaps
            gapDistributionData:
              result
                .find((chart) => chart.Name === "EclGapDistribution")
                ?.Data.map((item) => ({
                  interval: item.Interval,
                  count: item.SumFifoLine,
                })) || [],

            // Actuarial gap distribution - Histogram specifically for actuarial model gaps
            actuarialGapDistributionData:
              result
                .find((chart) => chart.Name === "EclActuarialGapDistribution")
                ?.Data.map((item) => ({
                  interval: item.Interval,
                  count: item.SumFifoLine,
                })) || [],

            // ECL Osiris system total
            eclOsirisData:
              result.find((chart) => chart.Name === "EclTotalSumOsiris")
                ?.Data[0] || null,

            // ECL CSA system total
            eclGroupData:
              result.find((chart) => chart.Name === "EclTotalSumInv")
                ?.Data[0] || null,

            // Total ECL gap (with sign)
            eclGapTotal:
              result.find((chart) => chart.Name === "EclGapTotal")?.Data[0] ||
              null,

            // Absolute ECL gap (magnitude only)
            eclGapTotalAbs:
              result.find((chart) => chart.Name === "EclGapTotalAbs")
                ?.Data[0] || null,
          };

          setChartData(transformedData);
        }
      } catch (error) {
        console.error("Error in the response:", error);
      }
    };

    fetchControlCharts();
  }, [selectedComputation, accessToken]);

  /**
   * Component render - Multi-section dashboard layout with various ECL gap visualizations
   * Organized in a responsive grid system using Bootstrap Row/Col components
   */
  return (
    <div>
      {/* Header Row - Computation Selector and Summary Carousels */}
      <Row className="g-3 mb-3">
        <Col md={4}>
          <PyramidCard className="h-100">
            <div className="d-flex flex-column h-100 py-2 justify-content-around">
              <div className="fw-medium fs-7 text-nowrap mt-n2">
                Computation
              </div>
              <SelectBox
                dataSource={groupedComputations}
                displayExpr="description"
                valueExpr="id"
                grouped={true}
                value={selectedComputation}
                onValueChanged={(e) => setSelectedComputation(e.value)}
                disabled={isLoading}
                searchEnabled={true}
                className="w-100"
              />
            </div>
          </PyramidCard>
        </Col>

        {/* ECL and Gap Carousels Section */}
        <CarouselSection
          eclOsirisData={chartData.eclOsirisData}
          eclGroupData={
            chartData.eclGroupData &&
              (selectedGroupDisplayName || companyList.length > 0)
              ? {
                ...chartData.eclGroupData,
                Title: `ECL - ${selectedGroupDisplayName || companyList[0].displayName
                  }`,
              }
              : chartData.eclGroupData
          }
          eclGapTotal={chartData.eclGapTotal}
          eclGapTotalAbs={chartData.eclGapTotalAbs}
        />
      </Row>

      {/* Security Breakdown - Full Width Section */}
      <Row className="g-3 mb-3">
        <Col xs={12}>
          <EclGapBySecurity
            data={chartData.securityData}
            palette={currentPalette}
          />
        </Col>
      </Row>

      {/* Rating Gap Analysis - Full Width Section */}
      <Row className="g-3 mb-3">
        <Col xs={12}>
          <EclGapByRating
            data={chartData.ratingsData}
            palette={currentPalette}
          />
        </Col>
      </Row>

      {/* Three-Column Layout - Instrument, Sector, and Bucket Gaps */}
      <Row className="g-3 mb-3">
        <Col md={4}>
          <EclGapByInstrumentType
            data={chartData.instrumentTypesData}
            palette={currentPalette}
          />
        </Col>
        <Col md={4}>
          <EclGapBySector
            data={chartData.sectorsData}
            palette={currentPalette}
          />
        </Col>
        <Col md={4}>
          <EclGapByBucket
            data={chartData.bucketsData}
            palette={currentPalette}
          />
        </Col>
      </Row>

      {/* Two-Column Layout - Gap Distribution Charts */}
      <Row className="g-3 mb-3">
        <Col md={6}>
          <EclGapDistribution
            data={chartData.gapDistributionData}
            palette={currentPalette}
          />
        </Col>
        <Col md={6}>
          <EclActuarialGapDistribution
            data={chartData.actuarialGapDistributionData}
            palette={currentPalette}
          />
        </Col>
      </Row>
    </div>
  );
};

export default ControlCharts;
